import { useState } from 'react'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

import { DocumentSignatory } from '../../../../lib/db/db.types'
import { WorkspaceCollaborator } from '../../../../store/slices/workspaceSlice.types'

type Props = {
  collaborators: WorkspaceCollaborator[]
  signatories: Record<string, DocumentSignatory>
  signatoryColors: Record<string, string>
  onChange: (signatories: Record<string, DocumentSignatory>) => void
}

export const ManageSigners = ({
  collaborators,
  signatories,
  signatoryColors,
  onChange,
}: Props) => {
  const [selectedSigners, setSelectedSigners] = useState<string[]>(
    Object.values(signatories)
      .sort((a, b) => a.order - b.order)
      .map((s) => s.id),
  )

  const handleCollaboratorToggle = (
    collaboratorId: string,
    email: string,
    name: string,
  ) => {
    let newSelectedSigners: string[]

    if (selectedSigners.includes(collaboratorId)) {
      newSelectedSigners = selectedSigners.filter((id) => id !== collaboratorId)
    } else {
      newSelectedSigners = [...selectedSigners, collaboratorId]
    }

    setSelectedSigners(newSelectedSigners)

    // Update signatories with new order
    const newSignatories: Record<string, DocumentSignatory> = {}
    newSelectedSigners.forEach((id, index) => {
      if (signatories[id]) {
        newSignatories[id] = { ...signatories[id], order: index }
      } else {
        newSignatories[id] = {
          id,
          email,
          name,
          order: index,
          status: 'pending',
        }
      }
    })

    onChange(newSignatories)
  }

  const handleDragEnd = (result: any) => {
    if (!result.destination) return

    const items = Array.from(selectedSigners)
    const [reorderedItem] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, reorderedItem)

    setSelectedSigners(items)

    // Update signatories with new order
    const newSignatories: Record<string, DocumentSignatory> = {}
    items.forEach((id, index) => {
      newSignatories[id] = { ...signatories[id], order: index }
    })

    onChange(newSignatories)
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="font-medium">Available Signers</h3>
        <div className="space-y-2">
          {collaborators.map((collaborator) => (
            <div key={collaborator.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedSigners.includes(collaborator.id)}
                onChange={() =>
                  handleCollaboratorToggle(
                    collaborator.id,
                    collaborator.email,
                    collaborator.name,
                  )
                }
              />
              <span>
                {collaborator.name} ({collaborator.email})
              </span>
            </div>
          ))}
        </div>
      </div>

      {selectedSigners.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-medium">Signing Order</h3>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="signers">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-2"
                >
                  {selectedSigners.map((id, index) => {
                    const collaborator = collaborators.find((c) => c.id === id)
                    if (!collaborator) return null

                    return (
                      <Draggable key={id} draggableId={id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="p-2 bg-gray-50 rounded flex items-center"
                            style={{
                              backgroundColor: signatoryColors[id],
                            }}
                          >
                            <span className="mr-2">{index + 1}.</span>
                            <span>
                              {collaborator.name} ({collaborator.email})
                            </span>
                          </div>
                        )}
                      </Draggable>
                    )
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
    </div>
  )
}
